"use client";

import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Switch,
  Slider,
  Paper,
  Grid,
  TextField,
  InputAdornment,
} from "@mui/material";
import SpeakerIcon from "@mui/icons-material/Speaker";

import { styled } from "@mui/material/styles";
import { MuiTelInput } from "mui-tel-input";
import { VenueFormData } from "../../../../types/venue";

import { RootState } from "../../../../store/Index";
import { useSelector } from "react-redux";

// ── types ──────────────────────────────────────────────────────────────────

interface PASystemsProps {
  formData: VenueFormData;
  handleInputChange: (
    fieldOrEvent:
      | keyof VenueFormData
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | { target: { name: string; value: string } },
    value?: string,
  ) => void;
  setFormData: React.Dispatch<React.SetStateAction<VenueFormData>>;
}

// ── styled components (OUTSIDE component to prevent remounting on each render) ──

const FieldLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "textcolor",
})<{ textcolor?: string }>(({ textcolor }) => ({
  fontSize: "13px",
  fontWeight: 500,
  opacity: 0.9,
  color: textcolor,
  marginBottom: "6px",
  lineHeight: 1.4,
}));

const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) =>
    prop !== "inputbg" && prop !== "textcolor" && prop !== "placeholdercolor",
})<{ inputbg?: string; textcolor?: string; placeholdercolor?: string }>(
  ({ inputbg, textcolor, placeholdercolor }) => ({
    width: "100%",
    "& .MuiOutlinedInput-root": {
      backgroundColor: inputbg,
      borderRadius: "30px",
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px 14px",
      fontSize: "13px",
      color: textcolor,
      "&::placeholder": {
        color: placeholdercolor,
        opacity: 0.9,
      },
    },
    "& .MuiFormHelperText-root": {
      marginLeft: 0,
      fontSize: "13px",
    },
  }),
);

// ── component ──────────────────────────────────────────────────────────────

export default function PASystems({
  formData,
  handleInputChange,
  setFormData,
}: PASystemsProps) {
  const reduxTheme = useSelector((state: RootState) => state.theme);

  // Derive theme colour shortcuts (matching VenueEssentials)
  const color100 = reduxTheme.primary_colors?.[100];
  const color300 = reduxTheme.primary_colors?.[300];
  const color700 = reduxTheme.primary_colors?.[700];
  const color800 = reduxTheme.primary_colors?.[800];
  const color400 = reduxTheme.primary_colors?.[400];
  const color900 = reduxTheme.primary_colors?.[900];

  const showPAFields = formData.has_pa_system === "yes";

  // ── fetch providers ────────────────────────────────────────────────────

  useEffect(() => {
    if (formData.has_pa_system === "no") {
      setFormData((prev) => ({
        ...prev,
        pa_system_provider: "",
        pa_system_distance_from_livestream: "",
        other_pa_system_service_provider: "",
        pa_system_contact_phone: "",
        pa_system_contact_email: "",
      }));
    }
  }, [formData.has_pa_system, setFormData]);

  // ── slider + switch ────────────────────────────────────────────────────

  const handleSliderChange = (_: Event, value: number | number[]) => {
    handleInputChange({
      target: {
        name: "pa_system_distance_from_livestream",
        value: String(value),
      },
    });
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleInputChange({
      target: { name: "has_pa_system", value: e.target.checked ? "yes" : "no" },
    });
  };

  const distanceValue =
    parseInt(formData.pa_system_distance_from_livestream || "5") || 5;

  // ── render ─────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* ── Has PA System Toggle ── */}
      <Paper
        sx={{
          p: 3,
          borderRadius: 3,
          backgroundColor: color800,
          mb: 4,
          boxShadow: "none",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                backgroundColor: color900,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SpeakerIcon sx={{ color: color100 }} />
            </Box>
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={500}
                fontSize="14px"
                sx={{ color: color100, opacity: 0.9 }}
              >
                Does venue have PA System?
              </Typography>
              <Typography
                variant="body2"
                fontSize="13px"
                sx={{ color: color700 }}
              >
                A PA system helps with audio distribution across the venue.
              </Typography>
            </Box>
          </Box>
          <Switch
            checked={formData.has_pa_system === "yes"}
            onChange={handleSwitchChange}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: color900,
              },

              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: color400,
                opacity: 1,
              },

              "& .MuiSwitch-switchBase.Mui-checked:hover + .MuiSwitch-track": {
                backgroundColor: color400,
              },
            }}
          />{" "}
        </Box>
      </Paper>

      {showPAFields && (
        <>
          {/* ── Distance from Livedesk ── */}
          <Box sx={{ mb: 4 }}>
            <FieldLabel textcolor={color100}>Distance from Livedesk</FieldLabel>
            {/* <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Slider
                  value={distanceValue}
                  onChange={handleSliderChange}
                  min={0}
                  max={100}
                  sx={{
                    color: color800,
                    "& .MuiSlider-thumb": {
                      backgroundColor: color400,
                      width: 15,
                      height: 15,
                    },
                  }}
                />
              </Box>
              <Chip
                label={`${distanceValue} meters`}
                sx={{ backgroundColor: color800, minWidth: 100 }}
              />
            </Box> */}

            <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Slider
                  value={Math.min(distanceValue, 100)}
                  onChange={handleSliderChange}
                  min={0}
                  max={100}
                  sx={{
                    color: color800,
                    "& .MuiSlider-thumb": {
                      backgroundColor: color400,
                      width: 15,
                      height: 15,
                    },
                  }}
                />
              </Box>

              <TextField
                value={formData.pa_system_distance_from_livestream ?? "5"}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const raw = e.target.value;
                  if (raw === "" || /^\d+$/.test(raw)) {
                    handleInputChange({
                      target: {
                        name: "pa_system_distance_from_livestream",
                        value: raw,
                      },
                    });
                  }
                }}
                size="small"
                inputProps={{
                  inputMode: "numeric",
                  style: {
                    textAlign: "center",
                    fontSize: "13px",
                    color: color700,
                    padding: "6px 8px",
                    width: "48px",
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography
                        fontSize="12px"
                        sx={{ color: color300, whiteSpace: "nowrap" }}
                      >
                        m
                      </Typography>
                    </InputAdornment>
                  ),
                  sx: {
                    backgroundColor: color800,
                    borderRadius: "30px",
                    "& fieldset": { border: "none" },
                    "&:hover fieldset": { border: "none" },
                    "&.Mui-focused fieldset": { border: "none" },
                  },
                }}
              />
            </Box>
          </Box>

          {/* ── PA System Provider + Other Details ── */}
          <Grid container spacing={3}>
            {/* PA System Provider — md:4 (1 col), add button stacked below, 2 cols left empty */}

            <Grid size={{ xs: 12, md: 4 }}>
              <FieldLabel textcolor={color100}>PA System Provider</FieldLabel>
              <StyledTextField
                name="pa_system_provider"
                placeholder="e.g. Sound Systems Ltd"
                value={formData.pa_system_provider || ""}
                onChange={handleInputChange}
                inputbg={color800}
                textcolor={color700}
                placeholdercolor={color300}
              />
            </Grid>

            {/* 2 empty columns — intentional gap */}
            <Grid
              size={{ xs: 0, md: 8 }}
              sx={{ display: { xs: "none", md: "block" } }}
            />

            {/* Other P.A System Provider */}
            <Grid size={{ xs: 12, md: 4 }}>
              <FieldLabel textcolor={color700}>
                Other P.A System Provider
              </FieldLabel>
              <StyledTextField
                name="other_pa_system_service_provider"
                placeholder="e.g. Sound Guy Audio Visuals"
                value={formData.other_pa_system_service_provider || ""}
                onChange={handleInputChange}
                inputbg={color800}
                textcolor={color700}
                placeholdercolor={color300}
              />
            </Grid>

            {/* Phone — MuiTelInput with country code picker */}
            <Grid size={{ xs: 12, md: 4 }}>
              <FieldLabel textcolor={color700}>Phone</FieldLabel>
              <MuiTelInput
                defaultCountry="ZW"
                value={formData.pa_system_contact_phone || ""}
                onChange={(value) =>
                  handleInputChange({
                    target: { name: "pa_system_contact_phone", value },
                  })
                }
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: color800,
                    borderRadius: "30px",
                    fontSize: "13px",
                    "& fieldset": { border: "none" },
                    "&:hover fieldset": { border: "none" },
                    "&.Mui-focused fieldset": { border: "none" },
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "10px 14px",
                    fontSize: "13px",
                    color: color700,
                    "&::placeholder": { color: color300, opacity: 0.9 },
                  },
                  "& .MuiTelInput-Flag": { mr: 0.5 },
                  "& .MuiButtonBase-root": {
                    color: color700,
                    ml: "4px",
                  },
                }}
              />
            </Grid>

            {/* Email */}
            <Grid size={{ xs: 12, md: 4 }}>
              <FieldLabel textcolor={color700}>Email</FieldLabel>
              <StyledTextField
                name="pa_system_contact_email"
                placeholder="e.g. provider@example.com"
                value={formData.pa_system_contact_email || ""}
                onChange={handleInputChange}
                type="email"
                inputbg={color800}
                textcolor={color700}
                placeholdercolor={color300}
              />
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}
